import React, {useEffect, useState} from 'react';
import {HashRouter, Route, Routes, useLocation} from 'react-router-dom';
import Header from './components/Header';
import LogEntryPage from './pages/LogEntryPage';
import ReportPage from './pages/ReportPage';
import SettingsPage from './pages/SettingsPage';
import {isSupabaseConfigured, supabase} from './lib/supabase';
import './App.css';

function ProtectedApp() {
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState('');

  async function loadData() {
    const [configResult, peopleResult] = await Promise.all([
      supabase.from('config').select('*').limit(1).maybeSingle(),
      supabase
        .from('people')
        .select('*')
        .order('sort_order', {ascending: true})
        .order('name')
    ]);

    const error = configResult.error || peopleResult.error;

    if (error) {
      setFatalError(error.message);
      setLoading(false);
      return;
    }

    if (!configResult.data) {
      setFatalError(
        'No configuration row was found. Run supabase/schema.sql first.'
      );
      setLoading(false);
      return;
    }

    setConfig(configResult.data);
    setPeople(peopleResult.data || []);
    setLoading(false);
  }

  useEffect(() => {
    loadData();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('./sw.js').catch(() => {});
    }
  }, []);

  async function configurationSaved() {
    await loadData();
  }

  if (loading) {
    return <div className="app-loading">Loading Time Tracker…</div>;
  }

  if (fatalError) {
    return (
      <div className="setup-message">
        <h1>Setup needed</h1>
        <p>{fatalError}</p>
      </div>
    );
  }

  const titles = {
    '/': 'Log Entry',
    '/report': 'View Report',
    '/settings': 'Settings'
  };

  return (
    <div className="app-shell">
      <Header
        config={config}
        pageTitle={titles[location.pathname] || 'Time Tracker'}
      />
      <main className="page-content">
        <Routes>
          <Route path="/" element={<LogEntryPage people={people} />} />
          <Route path="/report" element={<ReportPage />} />
          <Route
            path="/settings"
            element={
              <SettingsPage
                config={config}
                people={people}
                onSaved={configurationSaved}
              />
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  if (!isSupabaseConfigured) {
    return (
      <div className="setup-message">
        <h1>Connect Supabase</h1>
        <p>
          Copy <code>.env.example</code> to <code>.env</code>, add your Supabase
          URL and anonymous key, then run <code>supabase/schema.sql</code>.
        </p>
      </div>
    );
  }

  return (
    <HashRouter>
      <ProtectedApp />
    </HashRouter>
  );
}

export default App;